/**
 * Tutorial Steps — pure data defining the 13-step tutorial sequence (0-12).
 *
 * Every step requires player action. No auto-advance text dumps.
 * Narrative arc: power ramp (0-6) → reversal (7) → survival (8-9) → tools (10-11) → payoff (12).
 */

import { Keys } from 'bonkjs';
import { ACTION } from '../constants';
import { TUTORIAL } from '../constants';

// ─── Success Condition Types ────────────────────────────────────────────────

export type SuccessCondition =
  | { type: 'player_moved'; threshold: number }
  | { type: 'player_lifted' }
  | { type: 'player_fired' }
  | { type: 'opponent_hit' }
  | { type: 'player_fired_tier'; tier: number }
  | { type: 'opponent_tumbling' }
  | { type: 'edgeguard_fired' }
  | { type: 'player_hit' }
  | { type: 'di_input_detected' }
  | { type: 'di_window_resolved' }
  | { type: 'edgeguard_survived' }
  | { type: 'player_recovered' }
  | { type: 'burst_resolved' }
  | { type: 'opponent_ko' };

// ─── Step Interface ─────────────────────────────────────────────────────────

export interface TutorialStep {
  id: string;
  promptText: string;
  subText?: string;
  /** Keys to ADD to the unlock set on this step (cumulative). Uses bonkjs key codes. */
  unlockKeys: string[];
  condition: SuccessCondition;
  /** Seconds to hold after success before advancing (default: TUTORIAL.SUCCESS_HOLD_SECONDS) */
  holdAfterSuccess?: number;
  /** Force wind to this value for this step (null = inherit) */
  windOverride?: number | null;
  /** Set opponent damage % on step entry */
  opponentDamage?: number;
  /** Input hint labels shown on screen */
  inputHints: string[];
  /** If true, this step runs on the opponent's turn (player watches) */
  opponentTurn?: boolean;
  /** If true, freeze briefly on step entry for a teaching moment */
  freezeOnEntry?: boolean;
  /** Set player weaponCharge on step entry */
  playerCharge?: number;
}

// ─── Variant Constants ──────────────────────────────────────────────────────

/** First step index for the defense variant. */
export const DEFENSE_START_STEP = 7;

/** Number of steps in the offense variant (steps 0-6). */
export const OFFENSE_STEP_COUNT = 7;

/** Keys to pre-unlock for the defense variant (player can move/aim/fire freely). */
export const DEFENSE_PREUNLOCK_KEYS: string[] = [
  Keys.A, Keys.D, Keys.W, Keys.S, ACTION.JUMP, Keys.Digit2,
  // ACTION.FIRE and ACTION.CHARGE removed — unlocked at win_battle step
];

// ─── Step Definitions ───────────────────────────────────────────────────────

export const TUTORIAL_STEPS: TutorialStep[] = [
  // ═══ OFFENSE ARC (0-6) ═══

  // 0: Move + Jump
  {
    id: 'move_jump',
    promptText: 'Move with A/D. Jump with SPACE. Boost in the air.',
    unlockKeys: [Keys.A, Keys.D, ACTION.JUMP],
    condition: { type: 'player_lifted' },
    inputHints: ['A', 'D', 'SPACE'],
    windOverride: 0,
  },
  // 1: Burst
  {
    id: 'burst',
    promptText: 'Hold SHIFT + direction to Burst.',
    unlockKeys: [ACTION.SPECIAL],
    condition: { type: 'burst_resolved' },
    inputHints: ['SHIFT'],
    windOverride: 0,
  },
  // 2: Aim & Fire
  {
    id: 'aim_fire',
    promptText: 'Aim with your mouse. Hold LEFT CLICK to charge, release to fire.',
    subText: 'Longer charge = more distance',
    unlockKeys: [ACTION.FIRE, ACTION.CHARGE],
    condition: { type: 'player_fired' },
    inputHints: ['AIM', 'HOLD TO CHARGE'],
    windOverride: 0,
  },
  // 3: Hit + Edgeguard (guided freeze)
  {
    id: 'hit_edgeguard',
    promptText: 'Hit the opponent. After knockback, aim your RAILGUN and FIRE!',
    unlockKeys: [],
    condition: { type: 'edgeguard_fired' },
    opponentDamage: TUTORIAL.HIT_DEMO_DAMAGE,
    inputHints: [],
    windOverride: 0,
  },
  // 4: Wind Shot
  {
    id: 'wind_shot',
    promptText: 'Wind affects every shot. Watch the streaks. Adjust your aim.',
    unlockKeys: [],
    condition: { type: 'opponent_hit' },
    inputHints: [],
    windOverride: TUTORIAL.WIND_DEMO_VALUE,
  },
  // 5: Energy / Shot Tier
  {
    id: 'energy_tier',
    promptText: 'Your energy is full. Press 2 to select Shot 2, then fire!',
    unlockKeys: [Keys.Digit2],
    condition: { type: 'player_fired_tier', tier: 2 },
    inputHints: ['2'],
    windOverride: TUTORIAL.WIND_DEMO_VALUE,
    playerCharge: TUTORIAL.ENERGY_DEMO_CHARGE,
  },
  // 6: Kill
  {
    id: 'kill',
    promptText: 'Send them into the void. KO your opponent.',
    unlockKeys: [],
    condition: { type: 'opponent_ko' },
    holdAfterSuccess: 0,
    opponentDamage: TUTORIAL.KILL_DAMAGE,
    inputHints: [],
    windOverride: TUTORIAL.WIND_DEMO_VALUE,
  },

  // ═══ DEFENSE ARC (7-12) ═══

  // 7: Incoming Hit (Scene 1)
  {
    id: 'incoming_hit',
    promptText: "You're going to get hit. Learn how to survive if you hope to win.",
    unlockKeys: [],
    condition: { type: 'player_hit' },
    holdAfterSuccess: 0, // immediate transition to DI
    inputHints: [],
    windOverride: 0,
    opponentTurn: true,
  },
  // 8: DI Basics (Scene 2)
  {
    id: 'di_basics',
    promptText: 'You have a few frames to DI. Hold a direction to steer your knockback.',
    subText: 'This is DI. Use it every time you get launched.',
    unlockKeys: [],
    condition: { type: 'di_window_resolved' },
    holdAfterSuccess: 2.5, // let knockback play out before advancing
    inputHints: ['A', 'D'],
    windOverride: 0,
  },
  // 9: Strategic DI (Scene 3) — survive an edgeguard
  {
    id: 'di_strategic',
    promptText: 'DI at angles that make it hard for your opponent to Edge Guard.',
    unlockKeys: [],
    condition: { type: 'edgeguard_survived' },
    inputHints: ['A', 'D'],
    windOverride: 0,
    opponentTurn: true,
  },
  // 10: Recovery (Scene 4) — float + boost back to stage
  {
    id: 'recovery',
    promptText: 'After the Edge Guard, recover. Float with A/D. Tap SPACE for a boost.',
    subText: 'Two tools: air drift (slow, controlled) then rocket boost (fast, committed).',
    unlockKeys: [],
    condition: { type: 'player_recovered' },
    inputHints: ['A', 'D', 'SPACE'],
    windOverride: 0,
    freezeOnEntry: true,
  },
  // 11: Burst Escape (Scene 5) — last-resort SHIFT+W
  {
    id: 'burst_escape',
    promptText: 'When all else fails, SHIFT+W for a last-ditch Burst back on stage.',
    subText: 'Emergency move. Costs momentum.',
    unlockKeys: [ACTION.SPECIAL],
    condition: { type: 'burst_resolved' },
    inputHints: ['SHIFT', 'W'],
    windOverride: 0,
  },
  // 12: Win the Battle (Scene 6)
  {
    id: 'win_battle',
    promptText: 'Land safely. KO your opponent. Tutorial complete.',
    unlockKeys: [ACTION.FIRE, ACTION.CHARGE],
    condition: { type: 'opponent_ko' },
    holdAfterSuccess: 0, // triggers completion immediately
    opponentDamage: TUTORIAL.KILL_DAMAGE,
    inputHints: [],
    windOverride: 0,
  },
];
